/* SnapNPay, a Tulus Digital payment product: QR payments for participating local councils
 * and education providers, plus payment tools for organisations.
 * Source: "SnapNPay — Tulus Website Page Content & Handover" (14 October 2026), itself drawn
 * from snapnpay.my, its product pages and the app-store listings, with the council list and
 * the KPTM example supplied by Tulus. SnapNPay's payments go through the Tulus payment gateway
 * (Azrin, 14 October 2026), which the "Part of Tulus Digital" section says, linking /payment-gateway.
 *
 * Public since 2026-10-15: indexed, in the sitemap and on /penyelesaian (Azrin, 15 October 2026).
 *  - Approval to name and show them: SNAPNPAY_APPROVAL below (each organisation's agreement
 *    with Tulus covers it). Before a new council, school or agency goes on this page, check
 *    its agreement covers the same and say so there.
 *  - Left out on purpose, per the handover's own checklist: any council count (19 here, 35
 *    elsewhere), the dashboard's "real-time" tracking, SnapNShare's progress tracking,
 *    certification, settlement timing, volumes and ratings.
 *  - The app-store links use the official badges (components/StoreBadges.tsx). The logo and
 *    the three app screens are SnapNPay's own store images (Google Play listing and App Store
 *    splash screen, fetched 14 October 2026, on Azrin's instruction), shown as published.
 *    Screens naming organisations or a user's profile are left out. Not presented as an
 *    Islamic social finance app. */
import type { Language } from "../components/useLanguage";

/** The written approval to name and show the councils and KPTM on this page, in the shape
 * content/organisations.ts uses. */
export const SNAPNPAY_APPROVAL = {
  date: "2026-10-15",
  by: "Azrin Zainal Abidin",
  record: "Each organisation's agreement with Tulus for SnapNPay; Azrin confirmed on 2026-10-15 that every agreement covers the use of its name and logo.",
};

export const SNAPNPAY_KPTM = { name: "Kolej Poly-Tech MARA", short: "KPTM" };

/** KPTM case study, from "KPTM Case Study for the Tulus SnapNPay Product Page" (draft handoff,
 * 15 October 2026). Implementation details (ePay portal, FPX payment flow, the student-system
 * link, support and maintenance, the campus list) were supplied by Tulus and are on the
 * handoff's checklist to confirm with KPTM. Per-campus FPX set-up and Seller IDs are left out:
 * the page is for a general audience (Azrin, 15 October 2026), and the case study says "online
 * banking payment", "the student's record" and "looks after the service" rather than FPX, the
 * Student Management Information System, API support and server maintenance. No transaction counts, values,
 * dates or branch figures, by the handoff's own rule, with one exception: Tulus has served KPTM since 2019
 * (Azrin, 2026-10-21, to show long-term support), in the lead and the support card. The student figure is KPTMSB's (Kolej
 * Poly-Tech MARA Sdn Bhd), as its chief executive gave it to Bernama on 4 September 2026,
 * linked in place. KPTM's name and logo: see SNAPNPAY_APPROVAL. */
/** The same case study as a printable A4 page (2026-10-16), English and Malay. Their HTML sources
 * were not kept; on 2026-10-21 the "since 2019" line and support card were edited into the PDFs
 * themselves (Poppins, same positions), so a future rebuild must carry them. */
export const SNAPNPAY_KPTM_PDFS = { en: "/documents/kptm-snapnpay-case-study-en.pdf", ms: "/documents/kptm-snapnpay-case-study-ms.pdf" };
export const SNAPNPAY_KPTM_SOURCE = "https://bernama.com/en/news.php?id=2602896";
export const SNAPNPAY_KPTM_CAMPUSES = ["Kuala Lumpur", "Bangi", "Kuantan", "Kota Bharu", "Alor Setar", "Batu Pahat", "Ipoh"];

export const SNAPNPAY_LINKS = {
  appStore: "https://apps.apple.com/my/app/snapnpay-my/id1428868445",
  googlePlay: "https://play.google.com/store/apps/details?id=co.snapnpay.client.android&hl=en",
};

/** Active on SnapNPay per Tulus (handover, 14 October 2026); approved: SNAPNPAY_APPROVAL.
 * Names as each council writes them (the handover had "Tanjung Malim" and "Kota Semarahan").
 * Logos in public/images/snapnpay/councils/<short, lower case>.webp, fetched 14 October 2026
 * from the councils' own sites, the Kedah councils portal (pbt.kedah.gov.my) and Wikimedia
 * Commons, trimmed and resized to 160 px:
 *   pbt.kedah.gov.my: MBAS, MPKP, MPLBP (mplb.png), MPSPK (mpsp.png)
 *   council sites: MDTM (mdtm.gov.my /images/mdtm_2.png)
 *   Wikimedia Commons / Wikipedia: MBJB, MBKS, MBPG (same crest as mbpg.gov.my), MBPP (Seal of
 *   George Town), MDKP, MDKpr, MDLG, MDPH, MPK, MPKS, MPM, MPT, PBTP, MPS (same crest as
 *   mpsegamat.gov.my). MDKP (150 px), MDPH (180 px) and MPK (242 px) were the largest found.
 * KPTM's logo is from kptm.edu.my (KPTM-01). */
export const SNAPNPAY_COUNCILS: { name: string; short: string }[] = [
  { name: "Majlis Bandaraya Alor Setar", short: "MBAS" },
  { name: "Majlis Bandaraya Johor Bahru", short: "MBJB" },
  { name: "Majlis Bandaraya Kuching Selatan", short: "MBKS" },
  { name: "Majlis Bandaraya Pasir Gudang", short: "MBPG" },
  { name: "Majlis Bandaraya Pulau Pinang", short: "MBPP" },
  { name: "Majlis Daerah Kerian Perak", short: "MDKP" },
  { name: "Majlis Daerah Kampar", short: "MDKpr" },
  { name: "Majlis Daerah Lenggong", short: "MDLG" },
  { name: "Majlis Daerah Pengkalan Hulu Perak", short: "MDPH" },
  { name: "Majlis Daerah Tanjong Malim", short: "MDTM" },
  { name: "Majlis Perbandaran Kluang", short: "MPK" },
  { name: "Majlis Perbandaran Kubang Pasu", short: "MPKP" },
  { name: "Majlis Perbandaran Kota Samarahan", short: "MPKS" },
  { name: "Majlis Perbandaran Langkawi Bandaraya Pelancongan", short: "MPLBP" },
  { name: "Majlis Perbandaran Muar", short: "MPM" },
  { name: "Majlis Perbandaran Sungai Petani Kedah", short: "MPSPK" },
  { name: "Majlis Perbandaran Temerloh", short: "MPT" },
  { name: "Majlis Perbandaran Pengerang", short: "PBTP" },
  { name: "Majlis Perbandaran Segamat", short: "MPS" },
];

type Item = { title: string; body: string };

const EN = {
  meta: {
    title: "SnapNPay | QR Payments and Digital Payment Services by Tulus",
    description:
      "Pay local council bills and education fees with SnapNPay QR payments, for participating councils and student payments including KPTM, with payment tools for organisations from Tulus Digital.",
  },
  hero: {
    eyebrow: "A Tulus Digital payment product",
    title: "Local council and student payments, made simple.",
    lead: [
      "SnapNPay lets people pay participating local councils and education providers by QR. Scan the council's QR code, check your payment details and carry on through the online banking flow offered. SnapNPay also supports student payments, such as KPTM's.",
    ],
    badges: ["Participating local councils", "Education payments, including KPTM"],
    download: "Download SnapNPay",
    organisation: "Bring SnapNPay to your organisation",
    stores: "Available on iPhone and Android.",
    appStore: "Download SnapNPay on the App Store",
    googlePlay: "Get SnapNPay on Google Play",
    logoAlt: "SnapNPay, powered by Tulus",
    screenAlt: "The SnapNPay app's Imbas Kod QR (scan QR code) screen",
  },
  journey: {
    title: "A simple QR payment journey",
    intro: "From a council bill to a student payment, SnapNPay connects an organisation's QR code with a clear payment flow on your phone.",
    steps: [
      { body: "Open SnapNPay and scan the QR code or barcode." },
      { body: "Check the payment details shown." },
      { body: "Continue with the payment method offered, including FPX where available." },
      { body: "Complete the payment and see it confirmed." },
    ],
    screens: [
      { alt: "The SnapNPay sign-in screen", caption: "Open the app and sign in" },
      { alt: "The SnapNPay list of local councils, grouped by state", caption: "Find your council by state" },
    ],
  },
  everyday: {
    title: "QR payments for everyday services",
    items: [
      { title: "Local council payments", body: "Scan a participating council's payment QR code, check the payment details and pay through the flow offered." },
      { title: "Education payments", body: "Students at participating education providers, including KPTM, use the QR payment details they're given and pay through the online banking flow offered." },
      { title: "Bills and participating organisations", body: "Scan supported bills, notices and QR codes from participating organisations. Donations are one of the payments supported." },
    ] as Item[],
  },
  councils: {
    title: "Local councils on SnapNPay",
    intro: "Councils whose residents can pay through SnapNPay today.",
  },
  kptm: {
    eyebrow: "Case study",
    title: "Digital student fee payments across KPTM",
    lead: "KPTM students pay their fees online through the KPTM ePay portal, or by scanning a QR code in the SnapNPay app. Tulus has served KPTM since 2019.",
    aboutTitle: "About KPTM",
    about: "Kolej Poly-Tech MARA (KPTM) is a Malaysian private higher education institution managed by Kolej Poly-Tech MARA Sdn Bhd, a wholly owned subsidiary of MARA. Its programmes cover information technology, health sciences, social sciences, accounting, business management, engineering and bio-industry.",
    population: "Kolej Poly-Tech MARA Sdn Bhd has about 34,049 students across its seven locations",
    populationSource: "Bernama, 4 September 2026",
    journeyTitle: "How students pay",
    journey: [
      { body: "Students log in to the KPTM student payment portal with their student email address, choose what they're paying for and pay through SnapNPay." },
      { body: "Or they open the SnapNPay app and scan the payment QR code." },
    ],
    flowLabel: "How a fee payment flows",
    flow: ["Student", "KPTM ePay portal or SnapNPay app", "Online banking payment through SnapNPay", "Payment recorded on the student's KPTM account"],
    solutionTitle: "What SnapNPay provides",
    solution: [
      "SnapNPay handles the online banking payment behind the KPTM ePay portal, and links each payment to the student's record at KPTM.",
      "Tulus looks after the service for every participating campus and KPTM headquarters.",
    ],
    campusesLabel: "Participating campuses",
    valueTitle: "What KPTM gets",
    value: [
      { title: "Convenient online payments", body: "Students pay their fees online, from the portal or the app." },
      { title: "Connected student records", body: "Each payment shows up on the student's record at KPTM." },
      { title: "Support since 2019", body: "Tulus has kept the service running for the campuses and headquarters since 2019." },
      { title: "Easier fee checks", body: "With every payment on record, KPTM's finance team can check fees received more easily." },
    ] as Item[],
    action: "Talk to Tulus about digital payments",
    downloads: "Download the one-page case study",
    pdfEn: "English (PDF)",
    pdfMs: "Bahasa Malaysia (PDF)",
  },
  organisations: {
    title: "Payment tools for organisations",
    intro: "SnapNPay gives local councils, education providers and other organisations flexible ways to offer digital payments to residents, students, customers and supporters.",
    items: [
      { title: "QR payment journeys", body: "Put QR codes on bills, notices, printed material and eligible website checkouts to take people straight to a payment." },
      { title: "Website, portal and app integration", body: "Add the SnapNPay payment process to your website, portal or app through the SnapNPay API, on its own or together with QR codes." },
      { title: "FPX onboarding and integration support", body: "End-to-end support with FPX registration and integration for organisations starting out with digital payments." },
      { title: "Reconciliation support", body: "Help reviewing your payment records and finding discrepancies." },
    ] as Item[],
    action: "Talk to Tulus about SnapNPay",
  },
  snapnshare: {
    title: "SnapNShare: share a payment link or QR",
    body: "SnapNShare helps organisations share QR codes and payment links on WhatsApp, Facebook, Instagram and other channels, for fundraising and campaigns.",
    action: "Ask about SnapNShare",
  },
  tulus: {
    title: "Part of Tulus Digital",
    body: "SnapNPay is a digital payment product of Tulus Digital Sdn Bhd. It brings together a scan-to-pay mobile app with QR, API, FPX integration and reconciliation options for organisations. Payments made in SnapNPay go through the Tulus payment gateway.",
    link: "See all Tulus solutions",
    gateway: "About the Tulus payment gateway",
  },
  closing: {
    title: "Make local council and student payments simpler",
    body: "Residents and students: download SnapNPay. Councils and education providers: talk to us about bringing SnapNPay to your payers.",
    action: "Talk to Tulus about SnapNPay",
  },
};

export type SnapNPayCopy = typeof EN;

const MS: SnapNPayCopy = {
  meta: {
    title: "SnapNPay | Bayaran QR dan Perkhidmatan Pembayaran Digital oleh Tulus",
    description:
      "Bayar bil pihak berkuasa tempatan dan yuran pendidikan dengan bayaran QR SnapNPay, bagi majlis yang mengambil bahagian dan bayaran pelajar termasuk KPTM, dengan alat pembayaran untuk organisasi daripada Tulus Digital.",
  },
  hero: {
    eyebrow: "Produk pembayaran Tulus Digital",
    title: "Bayaran pihak berkuasa tempatan dan pelajar, dipermudahkan.",
    lead: [
      "SnapNPay membolehkan orang ramai membayar majlis pihak berkuasa tempatan dan penyedia pendidikan yang mengambil bahagian melalui QR. Imbas kod QR majlis, semak butiran bayaran anda dan teruskan melalui aliran perbankan dalam talian yang disediakan. SnapNPay juga menyokong bayaran pelajar, seperti bayaran KPTM.",
    ],
    badges: ["Pihak berkuasa tempatan yang mengambil bahagian", "Bayaran pendidikan, termasuk KPTM"],
    download: "Muat turun SnapNPay",
    organisation: "Bawa SnapNPay ke organisasi anda",
    stores: "Tersedia di iPhone dan Android.",
    appStore: "Muat turun SnapNPay di App Store",
    googlePlay: "Dapatkan SnapNPay di Google Play",
    logoAlt: "SnapNPay, dikuasakan oleh Tulus",
    screenAlt: "Skrin Imbas Kod QR dalam aplikasi SnapNPay",
  },
  journey: {
    title: "Perjalanan bayaran QR yang mudah",
    intro: "Daripada bil majlis hingga bayaran pelajar, SnapNPay menghubungkan kod QR organisasi dengan aliran bayaran yang jelas di telefon anda.",
    steps: [
      { body: "Buka SnapNPay dan imbas kod QR atau kod bar." },
      { body: "Semak butiran bayaran yang dipaparkan." },
      { body: "Teruskan dengan kaedah bayaran yang disediakan, termasuk FPX jika ada." },
      { body: "Lengkapkan bayaran dan lihat pengesahannya." },
    ],
    screens: [
      { alt: "Skrin log masuk SnapNPay", caption: "Buka aplikasi dan log masuk" },
      { alt: "Senarai pihak berkuasa tempatan dalam SnapNPay, mengikut negeri", caption: "Cari majlis anda mengikut negeri" },
    ],
  },
  everyday: {
    title: "Bayaran QR untuk perkhidmatan harian",
    items: [
      { title: "Bayaran pihak berkuasa tempatan", body: "Imbas kod QR bayaran majlis yang mengambil bahagian, semak butiran bayaran dan bayar melalui aliran yang disediakan." },
      { title: "Bayaran pendidikan", body: "Pelajar di penyedia pendidikan yang mengambil bahagian, termasuk KPTM, menggunakan butiran bayaran QR yang diberikan dan membayar melalui aliran perbankan dalam talian yang disediakan." },
      { title: "Bil dan organisasi yang mengambil bahagian", body: "Imbas bil, notis dan kod QR yang disokong daripada organisasi yang mengambil bahagian. Derma ialah salah satu bayaran yang disokong." },
    ],
  },
  councils: {
    title: "Pihak berkuasa tempatan di SnapNPay",
    intro: "Majlis yang penduduknya boleh membayar melalui SnapNPay hari ini.",
  },
  kptm: {
    eyebrow: "Kajian kes",
    title: "Bayaran yuran pelajar secara digital di seluruh KPTM",
    lead: "Pelajar KPTM membayar yuran secara dalam talian melalui portal ePay KPTM, atau dengan mengimbas kod QR dalam aplikasi SnapNPay. Tulus telah berkhidmat untuk KPTM sejak 2019.",
    aboutTitle: "Tentang KPTM",
    about: "Kolej Poly-Tech MARA (KPTM) ialah institusi pengajian tinggi swasta Malaysia yang diuruskan oleh Kolej Poly-Tech MARA Sdn Bhd, anak syarikat milik penuh MARA. Programnya merangkumi teknologi maklumat, sains kesihatan, sains sosial, perakaunan, pengurusan perniagaan, kejuruteraan dan bio-industri.",
    population: "Kolej Poly-Tech MARA Sdn Bhd mempunyai kira-kira 34,049 pelajar di tujuh lokasinya",
    populationSource: "Bernama, 4 September 2026",
    journeyTitle: "Cara pelajar membayar",
    journey: [
      { body: "Pelajar log masuk ke portal bayaran pelajar KPTM dengan e-mel pelajar mereka, memilih item yang hendak dibayar dan membayar melalui SnapNPay." },
      { body: "Atau mereka membuka aplikasi SnapNPay dan mengimbas kod QR bayaran." },
    ],
    flowLabel: "Aliran bayaran yuran",
    flow: ["Pelajar", "Portal ePay KPTM atau aplikasi SnapNPay", "Bayaran perbankan dalam talian melalui SnapNPay", "Bayaran direkodkan dalam akaun pelajar di KPTM"],
    solutionTitle: "Apa yang disediakan SnapNPay",
    solution: [
      "SnapNPay mengendalikan bayaran perbankan dalam talian di sebalik portal ePay KPTM, dan memadankan setiap bayaran dengan rekod pelajar di KPTM.",
      "Tulus menjaga perkhidmatan ini untuk setiap kampus yang mengambil bahagian dan ibu pejabat KPTM.",
    ],
    campusesLabel: "Kampus yang mengambil bahagian",
    valueTitle: "Manfaat untuk KPTM",
    value: [
      { title: "Bayaran dalam talian yang mudah", body: "Pelajar membayar yuran secara dalam talian, melalui portal atau aplikasi." },
      { title: "Rekod pelajar yang bersambung", body: "Setiap bayaran dipaparkan dalam rekod pelajar di KPTM." },
      { title: "Sokongan sejak 2019", body: "Sejak 2019, Tulus memastikan perkhidmatan ini terus berjalan untuk kampus dan ibu pejabat." },
      { title: "Semakan yuran lebih mudah", body: "Dengan setiap bayaran direkodkan, pasukan kewangan KPTM boleh menyemak yuran yang diterima dengan lebih mudah." },
    ],
    action: "Hubungi Tulus tentang bayaran digital",
    downloads: "Muat turun kajian kes satu halaman",
    pdfEn: "Bahasa Inggeris (PDF)",
    pdfMs: "Bahasa Malaysia (PDF)",
  },
  organisations: {
    title: "Alat pembayaran untuk organisasi",
    intro: "SnapNPay memberi pihak berkuasa tempatan, penyedia pendidikan dan organisasi lain cara yang fleksibel untuk menawarkan bayaran digital kepada penduduk, pelajar, pelanggan dan penyokong.",
    items: [
      { title: "Perjalanan bayaran QR", body: "Letakkan kod QR pada bil, notis, bahan bercetak dan halaman pembayaran laman web yang layak untuk membawa orang terus ke bayaran." },
      { title: "Integrasi laman web, portal dan aplikasi", body: "Tambah proses bayaran SnapNPay ke laman web, portal atau aplikasi anda melalui API SnapNPay, sendiri atau bersama kod QR." },
      { title: "Sokongan pendaftaran dan integrasi FPX", body: "Sokongan menyeluruh untuk pendaftaran dan integrasi FPX bagi organisasi yang baru memulakan bayaran digital." },
      { title: "Sokongan penyelarasan", body: "Bantuan menyemak rekod bayaran anda dan mengenal pasti percanggahan." },
    ],
    action: "Hubungi Tulus tentang SnapNPay",
  },
  snapnshare: {
    title: "SnapNShare: kongsi pautan bayaran atau QR",
    body: "SnapNShare membantu organisasi berkongsi kod QR dan pautan bayaran di WhatsApp, Facebook, Instagram dan saluran lain, untuk kutipan dana dan kempen.",
    action: "Tanya tentang SnapNShare",
  },
  tulus: {
    title: "Sebahagian daripada Tulus Digital",
    body: "SnapNPay ialah produk pembayaran digital Tulus Digital Sdn Bhd. Ia menggabungkan aplikasi imbas-dan-bayar dengan pilihan QR, API, integrasi FPX dan penyelarasan untuk organisasi. Bayaran yang dibuat dalam SnapNPay diproses melalui gerbang pembayaran Tulus.",
    link: "Lihat semua penyelesaian Tulus",
    gateway: "Tentang gerbang pembayaran Tulus",
  },
  closing: {
    title: "Permudahkan bayaran pihak berkuasa tempatan dan pelajar",
    body: "Penduduk dan pelajar: muat turun SnapNPay. Pihak berkuasa tempatan dan penyedia pendidikan: hubungi kami untuk membawa SnapNPay kepada pembayar anda.",
    action: "Hubungi Tulus tentang SnapNPay",
  },
};

export const SNAPNPAY: Record<Language, SnapNPayCopy> = { en: EN, ms: MS };
